import React, { useEffect, useState } from "react";
import { themeStyles } from "../utils/cardThemes";
import { generateQrDataUrl } from "./venue/shared/QrCodeGenerator";

// Compact landscape card in wallet-pass proportions (D-007).
//
// CardRenderer is the full portrait card page — identity block plus the whole
// link list, bio, contact box and QR. This is deliberately a separate
// component rather than another mode bolted onto it: the only thing the two
// share is the theme palette (imported from utils/cardThemes, not copied) and
// the avatar precedence rule. It renders the same fields a wallet pass
// carries, so the dashboard preview looks like what lands in Apple/Google
// Wallet.

/** The subset of a Card this component renders. Public fields only. */
export interface HorizontalCardData {
  name: string;
  position?: string;
  company?: string;
  profilePhoto?: string;
  squareLogo?: string;
  theme?: string;
  showProBadge?: boolean;
  badgeStyle?: string;
}

interface HorizontalCardProps {
  card: HorizontalCardData;
  /** Public card URL; rendered as the QR, mirroring the pass barcode. */
  publicUrl?: string;
  isPro?: boolean;
  /** Set false to render identity only (e.g. very narrow containers). */
  showQr?: boolean;
  className?: string;
}

const badgeRing: Record<string, string> = {
  gold: "ring-2 ring-amber-400",
  platinum: "ring-2 ring-slate-300",
  dark: "ring-2 ring-coffee-900",
};

/** Up to two initials, matching CardRenderer's fallback avatar. */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
}

const HorizontalCard: React.FC<HorizontalCardProps> = ({
  card,
  publicUrl,
  isPro = false,
  showQr = true,
  className = "",
}) => {
  const theme = themeStyles[card.theme || "default"] || themeStyles.default;
  const [qr, setQr] = useState<string | null>(null);

  useEffect(() => {
    if (!showQr || !publicUrl) {
      setQr(null);
      return;
    }
    let cancelled = false;
    generateQrDataUrl(publicUrl, 160)
      .then((dataUrl) => {
        if (!cancelled) setQr(dataUrl);
      })
      // A missing QR degrades the preview; it must never break the dashboard.
      .catch(() => {
        if (!cancelled) setQr(null);
      });
    return () => {
      cancelled = true;
    };
  }, [publicUrl, showQr]);

  const avatar = card.squareLogo || card.profilePhoto;
  const showRing = isPro && card.showProBadge !== false;
  const ring = showRing ? badgeRing[card.badgeStyle || "gold"] || badgeRing.gold : "";
  const subtitle = [card.position, card.company].filter(Boolean).join(" · ");

  return (
    <div
      className={`${theme.card} ${theme.text} rounded-xl shadow-coffee border border-black/5 p-4 flex items-center gap-4 w-full max-w-md ${className}`}
    >
      {avatar ? (
        <img
          src={avatar}
          alt=""
          className={`w-14 h-14 rounded-full object-cover flex-shrink-0 ${ring}`}
        />
      ) : (
        <div
          aria-hidden="true"
          className={`w-14 h-14 rounded-full flex-shrink-0 flex items-center justify-center text-cream-50 font-semibold ${theme.accent} ${ring}`}
        >
          {initialsOf(card.name)}
        </div>
      )}

      <div className="min-w-0 flex-1">
        <p className="font-semibold leading-tight truncate">{card.name}</p>
        {subtitle && <p className={`text-xs mt-0.5 truncate ${theme.subtext}`}>{subtitle}</p>}
        <p className={`text-[10px] font-mono uppercase tracking-[0.18em] mt-1.5 ${theme.subtext}`}>
          WhoBrew
        </p>
      </div>

      {qr && (
        <img
          src={qr}
          alt={`QR code linking to ${publicUrl}`}
          className="w-14 h-14 rounded bg-white p-0.5 flex-shrink-0"
        />
      )}
    </div>
  );
};

export default HorizontalCard;
